import L from "leaflet";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const STADIA_KEY = process.env.REACT_APP_STADIA_API_KEY;
export const STADIA_TILE_URL = `https://tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png?api_key=${STADIA_KEY}`;

export const BOARD_FONT = "'IBM Plex Mono', 'Consolas', 'Lucida Console', monospace";

export const FLASH_THRESHOLDS = { 63: 10, 84: 20, 82: 15, 81: 25 };

export const AVAILABLE_SIGNALS = [97, 98, 99, 89];

// Return current HH:MM:SS in America/New_York (dispatch-local) so stamps
// on user-typed entries match backend-generated ones (INITIAL CALL ENTRY,
// ASSIGNED, ASSIST, address updates). Fixes the bug where different rows
// showed UTC vs local time.
export function localHMS(d) {
  const dt = d instanceof Date ? d : new Date();
  return dt.toLocaleTimeString("en-GB", { timeZone: "America/New_York", hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

// City → borough abbreviation used everywhere addresses render for the crew.
// e.g., "1221 Fulton Street APT 4K, BK"
const BOROUGH_ABBR = {
  brooklyn: "BK", queens: "QN", "new york": "MN", manhattan: "MN",
  bronx: "BX", "staten island": "SI",
};

/**
 * Format an address for dispatch/crew display:
 *   "1221 Fulton Street APT 4K, BK"
 *   "Stuyvesant Avenue and Macon Street, BK"
 *
 * Expected input: backend already stripped state/country via its own
 * formatter (see DispatcherConsole.formatDispatchAddress). We try to:
 *   - identify the trailing token as a city name and convert to borough abbr
 *   - ensure a comma separates the street/apt portion from the city/borough
 *   - uppercase the whole thing for the retro MDT feel
 */
export function formatAddressShort(raw) {
  if (!raw) return "";
  let s = String(raw).trim().replace(/\s+/g, " ");
  // Strip trailing ", NY" / ", USA" if backend slipped any through.
  s = s.replace(/,\s*(NY|USA|US|UNITED STATES)$/i, "");
  // ─── Deduplicate repeated apartment/unit tokens ───
  // The geocoder and the manual APT field both tend to inject " APT 4K"
  // into the same string, yielding "... APT 4K APT 4K". Collapse to one.
  // Matches APT / APARTMENT / UNIT / STE / SUITE / # patterns repeated
  // back-to-back with the SAME identifier (case-insensitive).
  s = s.replace(
    /\b(APT|APARTMENT|UNIT|STE|SUITE|#)\s*([A-Z0-9-]+)(?:\s*,?\s*\1\s*\2\b)+/gi,
    "$1 $2"
  );
  // Also collapse simple duplicated substrings like "APT 4K APT 4K" that
  // didn't match the above (different casing/spacing edge cases).
  s = s.replace(/\b((?:APT|APARTMENT|UNIT|STE|SUITE)\s+[A-Z0-9-]+)(?:\s+\1)+\b/gi, "$1");
  // If there's already a comma + known city at the end, convert + keep comma.
  const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) {
    const last = parts[parts.length - 1];
    const abbr = BOROUGH_ABBR[last.toLowerCase()];
    const head = parts.slice(0, -1).join(", ");
    const tail = abbr || last.toUpperCase();
    return `${head}, ${tail}`.toUpperCase();
  }
  // No comma — try to split off a trailing borough/city word.
  const tokens = s.split(" ");
  for (let n = Math.min(2, tokens.length - 1); n >= 1; n--) {
    const candidate = tokens.slice(-n).join(" ").toLowerCase();
    if (BOROUGH_ABBR[candidate]) {
      const head = tokens.slice(0, -n).join(" ");
      return `${head}, ${BOROUGH_ABBR[candidate]}`.toUpperCase();
    }
  }
  return s.toUpperCase();
}

// Helper: combine `location` and `apt` fields safely, dropping the apt
// entirely when the location already contains that apartment number.
// Use this at every call site instead of `${loc} APT ${apt}`.
export function composeAddress(location, apt) {
  const loc = (location || "").trim();
  const a = (apt || "").trim();
  if (!a) return loc;
  // If the location already ends with APT X or contains " APT X" with the
  // same identifier, skip appending.
  const aptRe = new RegExp(`\\b(APT|APARTMENT|UNIT|STE|SUITE|#)\\s*${a.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&")}\\b`, "i");
  if (aptRe.test(loc)) return loc;
  return `${loc} APT ${a}`;
}


export function formatDuration(isoString) {
  if (!isoString) return "0";
  const start = new Date(isoString);
  const now = new Date();
  const diffMs = now - start;
  if (diffMs < 0) return "0";
  const totalMin = Math.floor(diffMs / 60000);
  if (totalMin < 60) return String(totalMin);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h${m}m`;
}

export function getSignalColor(signal) {
  switch (signal) {
    case 10: return "#FF00FF"; // DISP - magenta
    case 63: return "#00FF00";
    case 84: return "#FF0000";
    case 82: return "#FFFF00";
    case 81: return "#FF8800";
    case 89: return "#CC66FF";
    case 0: return "#FF3333";
    case 97: case 98: case 99: default: return "#FFFFFF";
  }
}

export function shouldFlashOrange(unit) {
  if (!unit || !unit.signal || !unit.status_changed_at) return false;
  const threshold = FLASH_THRESHOLDS[unit.signal];
  if (!threshold) return false;
  if (unit.status_acknowledged) return false;
  const elapsed = (Date.now() - new Date(unit.status_changed_at).getTime()) / 60000;
  return elapsed >= threshold;
}

export function unitAbbrev(name) {
  if (!name) return "?";
  const p = name.replace(/[-_]/g, " ").split(/\s+/);
  if (p.length >= 3) return (p[1][0] + p[p.length - 1]).toUpperCase();
  if (p.length === 2) return (p[0][0] + p[1]).toUpperCase();
  return name.substring(0, 2).toUpperCase();
}

export function makeAmbIcon(label) {
  return new L.DivIcon({
    html: `<div style="width:28px;height:28px;background:#00BFFF;border:2px solid #FFF;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.5);font-family:'IBM Plex Mono',monospace;font-size:10px;font-weight:700;color:#000">${label}</div>`,
    className: "", iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -16],
  });
}

export const hospitalIcon = new L.DivIcon({
  html: `<div style="width:24px;height:24px;background:#FF0000;border:2px solid #FFF;border-radius:4px;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 6px rgba(0,0,0,0.5)">
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><rect x="10" y="4" width="4" height="16" rx="1" fill="white"/><rect x="4" y="10" width="16" height="4" rx="1" fill="white"/></svg>
  </div>`,
  className: "", iconSize: [24, 24], iconAnchor: [12, 12], popupAnchor: [0, -14],
});

// Emergency call location: blue filled circle with a white medical cross.
// Used to mark confirmed / geocoded call addresses on the dispatcher map and GPS map.
export const emergencyIcon = new L.DivIcon({
  html: `<div style="width:30px;height:30px;background:#1E90FF;border:2px solid #FFF;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,0.6)">
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><rect x="10" y="3" width="4" height="18" rx="1" fill="white"/><rect x="3" y="10" width="18" height="4" rx="1" fill="white"/></svg>
  </div>`,
  className: "", iconSize: [30, 30], iconAnchor: [15, 15], popupAnchor: [0, -16],
});

export const redIcon = new L.Icon({
  iconUrl: "https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34], shadowSize: [41, 41],
});

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});
